/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {EntityTreeBranchModel} from '../models/entity-tree-branch.model';
import {EntityElementModel} from '../entity-element/models/entity-element.model';
import {TreeNodeModel} from '../../../settings/business-settings/models/tree-node-model';
import {EntityTypeEnum} from '../../../settings/business-settings/configuration/v1/entity-type.enum';
import {EntityTreeModel} from '../models/entity-tree.model';
import {ContextMenuActionEnum} from '../entity-element/models/context-menu-action.enum';
import {EntityTreeBranchDisplayDataModel} from '../../../settings/business-settings/configuration/v1/models/entity-tree-branch-display-data.model';

@Injectable({
    providedIn: 'root'
})
export class EntityTreeUtilService {
    /**
     * Checks if an entity has selected connections or path node
     * @param entity The {@link EntityTreeBranchModel} to check
     * @return boolean
     */
    hasSelectedPathNodeOrConnections(entity: EntityTreeBranchModel): boolean {
        return this.hasSelectedPathNode(entity) || this.hasSelectedConnections(entity);
    }

    /**
     * Checks if an entity has a path node or connections in its structure
     * @param entityData The {@link EntityTreeBranchDisplayDataModel} to check
     * @return boolean
     */
    entityHasPathOrConnections(entityData: EntityTreeBranchDisplayDataModel): boolean {
        if (entityData == null) {
            return false;
        }
        return entityData.hasPath || entityData.hasConnections;
    }

    /**
     * Checks if and entity has selected connections
     * @param entity The {@link EntityTreeBranchModel} to check
     * @return boolean
     */
    hasSelectedConnections(entity: EntityTreeBranchModel): boolean {
        if (entity != null && entity.connections != null) {
            return entity.connections.length > 0;
        }
        return false;
    }

    /**
     * Checks if an entity has a selected path node
     * @param entity The {@link EntityTreeBranchModel} to check
     * @return boolean
     */
    hasSelectedPathNode(entity: EntityTreeBranchModel): boolean {
        return entity != null && entity.path != null;
    }

    /**
     * Updates the equivalent pool item of a connection that was removed from the section
     * @param poolItem The pool item que update
     */
    removeFromConnectionsUpdatePoolItem(poolItem: EntityElementModel): void {
        poolItem.displayParentCode = null;
        poolItem.parentCode = null;
        poolItem.connectedWithParent = false;
    }

    /**
     * Updates the relation tree after removing a connection
     * @param element the element to be removed from the connections
     * @param entity the entity to which the new element belongs
     * @param configurationTreeNode The configuration tree node
     * @param tree the entity tree with the branches
     * @param dataTree The tree {@link EntityTreeModel}
     */
    removeFromConnectionsUpdateRelationTree(element: EntityElementModel,
                                            entity: EntityTreeBranchModel,
                                            configurationTreeNode: TreeNodeModel,
                                            tree: EntityTreeModel,
                                            dataTree: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>): void {
        if (element != null && entity != null && configurationTreeNode != null && tree != null) {
            const node = this.getCurrentEntityTreeNode(entity, configurationTreeNode, tree, dataTree);
            if (node != null) {
                node.relationTree.splice(node.relationTree.findIndex((item) => item.code === element.code), 1);
            }
        }
    }

    /**
     * Updates the relation tree recursively removing all the nodes where the element has a specific code.
     * @param code the code to be removed.
     * @param tree the tree node model.
     */
    removeAllElementCodeFromRelationTree(code: string, tree: TreeNodeModel[]): void {
        if (tree != null) {
            tree.forEach((item, index) => {
                if (item.code === code) {
                    tree.splice(index, 1);
                } else {
                    this.removeAllElementCodeFromRelationTree(code, item.relationTree);
                }
            });
        }
    }

    /**
     * Updates the relation tree after adding a connection
     * @param newElement the new element to be added to the connections
     * @param entity the entity to which the new element belongs
     * @param configurationTreeNode The configuration tree node
     * @param tree the entity tree with the branches
     * @param dataTree The tree {@link EntityTreeModel}
     */
    addToConnectionsUpdateRelationTree(newElement: EntityElementModel,
                                       entity: EntityTreeBranchModel,
                                       configurationTreeNode: TreeNodeModel,
                                       tree: EntityTreeModel,
                                       dataTree: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>): void {
        if (newElement != null && configurationTreeNode != null && entity != null && tree != null) {
            const entityHasSingleConnection = this.entityHasSingleConnection(entity, tree, dataTree);

            const node = this.getCurrentEntityTreeNode(entity, configurationTreeNode, tree, dataTree);

            if (node == null) {

                const parentType = dataTree.get(entity.name).pathFrom.find((item) => item.active).type;
                const parent = this.getEntityBranch(parentType, tree)
                        .get(parentType);

                const newNode: TreeNodeModel = {
                    code: parent.path.code,
                    type: parent.name,
                    relationTree: [{
                        code: newElement.code,
                        type: entity.name,
                        relationTree: [],
                        activeChildren: entity.pathTo?.filter((child) => child.active)
                                .map((child) => child.type)
                    }]
                };

                if (entityHasSingleConnection) {
                    configurationTreeNode.relationTree = [newNode];
                } else {
                    configurationTreeNode.relationTree.push(newNode);
                }
            } else {
                if (entityHasSingleConnection) {
                    node.relationTree = [{
                        code: newElement.code,
                        type: entity.name,
                        relationTree: [],
                        activeChildren: entity.pathTo?.filter((child) => child.active)
                                .map((child) => child.type)
                    }];
                } else {
                    node.relationTree.push({
                        code: newElement.code,
                        type: entity.name,
                        relationTree: [],
                        activeChildren: entity.pathTo?.filter((child) => child.active)
                                .map((child) => child.type)
                    });
                }
            }
        }
    }

    /**
     * Returns the branch to which the entity belongs
     * @param entity the entity type to get the branch
     * @param tree the entity tree with the branches
     */
    getEntityBranch(entity: EntityTypeEnum, tree: EntityTreeModel): Map<EntityTypeEnum, EntityTreeBranchModel> {
        let branch = null;
        // If entity is in common branch
        if (tree.commonBranch.get(entity) != null) {
            branch = tree.commonBranch;
            // Else if entity is in specific branch
        } else {
            tree.specificBranches.forEach((value) => {
                if (value.get(entity) != null) {
                    branch = value;
                }
            });
        }
        return branch;
    }

    /**
     * For a specific entity, returns the respective tree node, respecting all the tree path
     * @param entity the entity to get the node
     * @param configurationTree the relations configuration tree
     * @param tree the tree to which the entity belongs
     * @param dataTree The tree {@link EntityTreeModel} from each to retrieve the entity
     * @return the entity's related configurationTree node
     * */
    getCurrentEntityTreeNode(entity: EntityTreeBranchModel, configurationTree: TreeNodeModel, tree: EntityTreeModel,
                             dataTree: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>): TreeNodeModel {
        const relations = this.getEntityRelationPath(entity, [], tree, dataTree);
        let node: TreeNodeModel = null;

        if (relations != null) {
            relations.reverse();
            for (let level = 0; level < relations.length; level++) {
                if (node == null) {
                    node = configurationTree.relationTree.find((item) => item.code === relations[level]);
                } else {
                    if (node.relationTree != null) {
                        node = node.relationTree.find((item) => item.code === relations[level]);
                    }
                }
            }
        }
        return node;
    }

    /**
     * For a given {@link TreeNodeModel} replaces a specific tree node code
     * @param newCode The code from the updated element value
     * @param originalCode The code from the original element value
     * @param treeNode the {@link TreeNodeModel}
     */
    replaceRelationTreeNodesCodes(newCode: string, originalCode: string, treeNode: TreeNodeModel): void {
        if (treeNode.code != null && treeNode.code === originalCode) {
            treeNode.code = newCode;
        }
        if (treeNode.relationTree != null) {
            treeNode.relationTree.forEach((child) => {
                this.replaceRelationTreeNodesCodes(newCode, originalCode, child);
            });
        }
    }

    /**
     * Returns an entity configuration
     * @param entity The entity type {@link EntityTypeEnum} to retrieve
     * @param tree The tree {@link EntityTreeModel} from each to retrieve the entity
     */
    getEntityConfiguration(entity: EntityTypeEnum, tree: EntityTreeModel): EntityTreeBranchModel {
        return this.getEntityBranch(entity, tree)
                .get(entity);
    }

    /**
     * Returns an entity display data
     * @param entity The entity type {@link EntityTypeEnum} to retrieve
     * @param dataTree The tree {@link EntityTreeModel} from each to retrieve the entity
     */
    getEntityDisplayData(entity: EntityTypeEnum,
                         dataTree: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>): EntityTreeBranchDisplayDataModel {
        return dataTree.get(entity);
    }

    /**
     * Returns the default active value for a pathFrom from a specific entity
     * @param entity The entity type {@link EntityTypeEnum}
     * @param pathFromType The pathFrom type {@link EntityTypeEnum}
     * @param tree The tree {@link EntityTreeModel} from each to retrieve the default value
     */
    getPathFromDefaultActive(entity: EntityTypeEnum, pathFromType: EntityTypeEnum, tree: EntityTreeModel): boolean {
        let defaultPathFromNode;
        const defaultPathFrom = this.getEntityConfiguration(entity, tree).pathFrom;
        if (defaultPathFrom != null) {
            defaultPathFromNode = defaultPathFrom.find((item) => item.type === pathFromType);
        }
        if (defaultPathFromNode != null) {
            return defaultPathFromNode.active;
        }
        return false;
    }

    /**
     * Returns the default active value for a pathTo from a specific entity
     * @param entity The entity type {@link EntityTypeEnum}
     * @param pathToType The pathTo type {@link EntityTypeEnum}
     * @param tree The tree {@link EntityTreeModel} from each to retrieve the default value
     */
    getPathToDefaultActive(entity: EntityTypeEnum, pathToType: EntityTypeEnum, tree: EntityTreeModel): boolean {
        let defaultPathToNode;
        const defaultPathTo = this.getEntityConfiguration(entity, tree).pathTo;
        if (defaultPathTo != null) {
            defaultPathToNode = defaultPathTo.find((item) => item.type === pathToType);
        }

        if (defaultPathToNode != null) {
            return defaultPathToNode.active;
        }
        return false;
    }

    /**
     * Checks if and entity has multiple exclusive children
     * @param entity The {@link EntityTreeBranchModel} to check
     * @return boolean if the entity has multiple exclusive children
     * */
    entityHasMultipleExclusiveChildren(entity: EntityTreeBranchModel): boolean {
        let exclusiveChildren = false;
        if (entity.pathTo != null && entity.pathTo.length > 1) {
            for (let i = 0; i < entity.pathTo.length; i++) {
                exclusiveChildren = entity.pathTo[i].excludes != null && entity.pathTo
                        .find((child) => child.type === entity.pathTo[i].excludes) != null;
                if (exclusiveChildren) {
                    break;
                }
            }
        }
        return exclusiveChildren;
    }

    /**
     * Resets the tree paths to the original configuration starting from a given entity
     * @param node the {@link EntityTypeEnum} from which to start the paths reset
     * @param tree the {@link EntityTreeModel} with the original path configuration
     * @param dataTree the dataTree here the paths should be reset
     * */
    resetPathsToOriginalConfigurationFromEntity(node: EntityTypeEnum, tree: EntityTreeModel,
                                                dataTree: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>): void {
        const displayData = this.getEntityDisplayData(node, dataTree);
        const configurationData = this.getEntityConfiguration(node, tree);

        if (displayData != null && displayData.pathTo != null) {
            displayData.pathTo.forEach((pathToEntry) => {
                pathToEntry.active = configurationData.pathTo.find((entry) => entry.type === pathToEntry.type).active;

                const childConfigurationData = this.getEntityConfiguration(pathToEntry.type, tree);
                const childDisplayData = this.getEntityDisplayData(pathToEntry.type, dataTree);
                if (childDisplayData != null && childDisplayData.pathFrom != null) {
                    childDisplayData.pathFrom.forEach((pathFromEntry) =>
                            pathFromEntry.active = childConfigurationData.pathFrom
                                    .find((entry) => entry.type === pathFromEntry.type).active);
                }
                this.resetPathsToOriginalConfigurationFromEntity(pathToEntry.type, tree, dataTree);
            });
        }
    }

    /**
     * Checks if an entity is configured to have a single connection
     * @param entity the {@link EntityTreeBranchModel} of the entity to check
     * @param tree the tree to which the entity belongs
     * @param dataTree The tree {@link EntityTreeModel}
     */
    entityHasSingleConnection(entity: EntityTreeBranchModel, tree: EntityTreeModel,
                              dataTree: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>): boolean {
        const parent = this.getActiveParentEntity(entity, tree, dataTree);
        if (parent != null) {
            const childEntity = parent.pathTo.find((child) => child.type === entity.name);
            if (childEntity != null) {
                return childEntity.hasSingleConnectedChild;
            }
        }
        return false;
    }

    /**
     * Returns the active parent entity an entity. If the entity has no parent returns null
     * @param entity The entity {@link EntityTreeBranchModel}
     * @param tree the tree to which the entity belongs
     * @param dataTree The tree {@link EntityTreeModel}
     * @return EntityTreeBranchModel the parent entity
     */
    getActiveParentEntity(entity: EntityTreeBranchModel, tree: EntityTreeModel,
                          dataTree: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>): EntityTreeBranchModel {
        if (entity != null && entity.pathFrom != null) {

            const parentEntity = this.getEntityDisplayData(entity.name, dataTree).pathFrom
                    .find((item) => item.active);

            if (parentEntity != null) {
                return this.getEntityBranch(parentEntity.type, tree)
                        .get(parentEntity.type);
            }

        }
        return null;
    }

    /**
     * For a given entity and tree, return if it allows only for a single connection and if it has that connection selected
     * @param entity the entity to check
     * @param tree the tree to which the entity belongs
     * @param dataTree The tree {@link EntityTreeModel}
     */
    entityHasSingleConnectionAndSelectedConnection(entity: EntityTreeBranchModel,
                                                   tree: EntityTreeModel,
                                                   dataTree: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>): boolean {
        return this.entityHasSingleConnection(entity, tree, dataTree)
                && this.hasSelectedConnections(entity);
    }

    /**
     * For a given entity returns if its connections are not null
     * @param entity the entity to check
     * @param entityData the entity to check
     */
    hasNotNullConnections(entity: EntityTreeBranchModel, entityData: EntityTreeBranchDisplayDataModel): boolean {
        return entityData.hasConnections && entity.connections != null;
    }

    /**
     * For a given entity returns if its path is not null
     * @param entityData the entity to check
     * @param entity the entity to check
     */
    hasNotNullPath(entity: EntityTreeBranchModel, entityData: EntityTreeBranchDisplayDataModel): boolean {
        return entityData.hasPath && entity.path != null;
    }

    /**
     * Removes a given context menu action from the actions array
     * @param contextMenuActions the array from which to remove the actions
     * @param action the action to remove from the array
     */
    removeMenuAction(contextMenuActions: ContextMenuActionEnum[], action: ContextMenuActionEnum): void {
        if (contextMenuActions.includes(action)) {
            contextMenuActions.splice(contextMenuActions
                    .findIndex((entry) => entry === action), 1);
        }
    }

    /**
     * Adds a given context menu action to the actions array
     * @param contextMenuActions the array from which to add the actions
     * @param action the action to add to the array
     */
    addMenuAction(contextMenuActions: ContextMenuActionEnum[], action: ContextMenuActionEnum): void {
        if (!contextMenuActions.includes(action)) {
            contextMenuActions.push(action);
        }
    }

    /**
     * Returns if an entity pool item can be added to the path with the current parent
     * @param entity the entity to which the pool item belongs
     * @param item the pool item
     * @param parent the parent entity
     */
    entityPoolItemCanBeAddToPathWithParent(entity: EntityTreeBranchModel,
                                           item: EntityElementModel,
                                           parent: EntityTreeBranchModel): boolean {
        return parent == null || parent.path == null
                || (entity.hasMultipleConnectedParents || (!item.connectedWithParent || (item.parentCode === parent.path.code)));
    }

    /**
     * Returns if a pool item can be added to the connections of its entity
     * @param entity the entity to which the pool item belongs
     * @param item the pool item
     */
    entityPoolItemCanBeConnectedWithParent(entity: EntityTreeBranchModel, item: EntityElementModel): boolean {
        return entity.hasMultipleConnectedParents || !item.connectedWithParent;
    }

    /**
     * Return is the pool item is in the path
     * @param poolItemCode the pool item code
     * @param entityPathCode the code of the item currently in the path
     */
    poolItemIsInPath(poolItemCode: string, entityPathCode: string): boolean {
        return poolItemCode === entityPathCode;
    }

    /**
     * Enables an entity considering its parent state and its own active state on the parents pathTo array
     * @param entity The entity {@link EntityTreeBranchModel}
     * @param tree the tree to which the entity belongs
     * @param dataTree The tree {@link EntityTreeModel}
     */
    enableEntity(entity: EntityTreeBranchModel,
                 dataTree: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>,
                 tree: EntityTreeModel): boolean {
        const entityDisplayData = this.getEntityDisplayData(entity.name, dataTree);

        if (entity.pathFrom != null && this.retrieveEntryFromArrayByActive(entityDisplayData.pathFrom, true) == null) {
            return false;
        }

        const parent = this.getActiveParentEntity(entity, tree, dataTree);
        const parentData = this.getEntityDisplayData(parent?.name, dataTree);
        if (parentData != null) {
            if (parentData.hasPath) {
                return parent.path != null;
            } else if (parentData.hasConnections) {
                return parent.connections.length > 0;
            }
        }
        return true;
    }

    /**
     * Returns if an entity has any active children
     */
    entityHasActiveChildren(entity: EntityTypeEnum,
                            dataTree: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>): boolean {
        const entityData = this.getEntityDisplayData(entity, dataTree);
        return entityData.pathTo != null && entityData.pathTo.some((child) => child.active);
    }

    /**
     * Retrieves an entry from an array by the active value
     * @param array the array from which to retrieve the entry
     * @param active the active value of the entry to retrieve
     */
    retrieveEntryFromArrayByActive<T = any>(array: T[], active: boolean): T | null {
        return array.find((item) => item['active'] === active);
    }

    entityIsInitialized(entity: EntityTreeBranchModel, entityData: EntityTreeBranchDisplayDataModel): boolean {
        return (entityData.hasPath && entity.path != null)
                || (!entityData.hasPath && entityData.hasConnections
                        && entity.connections != null && entity.connections.length > 0);
    }

    /**
     * Returns the active or inactive parent entity an entity.
     * If there's an active parent, returns the active parent. Otherwise, returns the 1st inactive parent.
     * @param entity The entity {@link EntityTreeBranchModel}
     * @param tree the tree to which the entity belongs
     * @param dataTree The tree {@link EntityTreeModel}
     * @return EntityTreeBranchModel the parent entity
     */
    getActiveOrInactiveParent(entity: EntityTreeBranchModel, tree: EntityTreeModel,
                              dataTree: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>): EntityTreeBranchModel {
        let parent = this.getActiveParentEntity(entity, tree, dataTree);

        if (parent == null && entity != null && entity.pathFrom != null) {
            const inactiveParent = this.getEntityBranch(
                    entity.pathFrom[0].type, tree)
                    .get(entity.pathFrom[0].type);
            parent = inactiveParent != null && inactiveParent.pathTo.length > 0 ? inactiveParent : null;

        }

        return parent;
    }

    isFirstItemInBranch = (branchOrderMap: Map<EntityTypeEnum, number>, key: EntityTypeEnum): boolean =>
            branchOrderMap.get(key) === 1;

    isLastItemInBranch = (treeBranchConfiguration: Map<EntityTypeEnum, EntityTreeBranchModel>,
                          branchOrderMap: Map<EntityTypeEnum, number>,
                          key: EntityTypeEnum): boolean => branchOrderMap.get(key) === treeBranchConfiguration.size;

    lastValueInBranch = (treeBranchConfiguration: Map<EntityTypeEnum, EntityTreeBranchModel>,
                         branchOrderMap: Map<EntityTypeEnum, number>): EntityTreeBranchModel => {
        let lastItem: EntityTreeBranchModel = null;
        treeBranchConfiguration.forEach((value, key) => {
            if (branchOrderMap.get(key) === treeBranchConfiguration.size) {
                lastItem = value;
            }
        });
        return lastItem;
    };

    firstValueInBranch = (treeBranchConfiguration: Map<EntityTypeEnum, EntityTreeBranchModel>,
                          branchOrderMap: Map<EntityTypeEnum, number>): EntityTreeBranchModel => {
        let firstItem: EntityTreeBranchModel = null;
        treeBranchConfiguration.forEach((value, key) => {
            if (branchOrderMap.get(key) === 1) {
                firstItem = value;
            }
        });
        return firstItem;
    };


    /**
     * Returns if a given entity is in an entity tree commonBranch
     */
    entityIsFromCommonBranch(entity: EntityTypeEnum, entityTree: EntityTreeModel): boolean {
        return entityTree.commonBranch.get(entity) != null;
    }

    /**
     * Returns if a given entity tree has specific branches
     */
    entityTreeHasSpecificBranches(entityTree: EntityTreeModel): boolean {
        return entityTree.specificBranchOptions != null && entityTree.specificBranchOptions.length > 0;
    }

    /**
     * For a given entity and tree, will retrieve the relation path from the specified entity to the root entity from that tree
     * @param entity the entity to get the path from
     * @param tree the tree to which the entity belongs
     * @param array the array into which the path should be added
     * @return string[] a string array with the path.codes from the specified entity to the root entity from the tree
     * @param dataTree The tree {@link EntityTreeModel}
     */
    private getEntityRelationPath(entity: EntityTreeBranchModel, array: string[], tree: EntityTreeModel,
                                  dataTree: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>): string[] {
        if (entity?.pathFrom != null) {

            const entityPathFromDisplayData = this.getEntityDisplayData(entity.name, dataTree).pathFrom;
            let parentFromPath = entityPathFromDisplayData.find((item) => item.active);
            if (entityPathFromDisplayData.length === 1 && parentFromPath == null) {
                parentFromPath = entityPathFromDisplayData.find((item) => !item.active);
            }

            if (parentFromPath != null) {
                const parent = parentFromPath.type;

                const parentEntity = this.getEntityBranch(parent, tree)
                        .get(parent);
                const parentEntityData = this.getEntityDisplayData(parentEntity.name, dataTree);
                if (parentEntityData.hasPath && parentEntity.path != null) {
                    array.push(parentEntity.path.code);
                }

                this.getEntityRelationPath(parentEntity, array, tree, dataTree);
            }
        }
        return array;
    }
}
